import React, { useMemo, useEffect, useState } from 'react';

import { Box } from '@mui/material';

import { IFiltersHistory, IEventTypes } from 'interfaces/patients/patientInterfaces';

import { MultiCombobox } from '@bestdoctor/spectrum-uikit/MultiCombobox';
import { DatePicker } from '@bestdoctor/spectrum-uikit/DatePicker';
import type { ComboboxOption } from '@bestdoctor/spectrum-uikit/Combobox';
import { format, parse, isValid } from 'date-fns';
import { isDateActual } from 'utils/dateHelpers';
import { TableHistory } from '../TableHistory/TableHistory';

export const FiltersHistory: React.FC<IFiltersHistory> = ({
  historyActions,
  hasNextPageCompanyActions,
  isFetchingNextPageCompanyActions,

  eventTypes,

  startDate,
  endDate,
  handleStartDate,
  handleEndDate,
  eventType,
  handleEventType,

  isLoadingCompanyActions,
  fetchNextPageCompanyActions,

  orderingTableHistory,
  handleOrderingTableHistory,
}) => {
  // Преобразуем Date в строку формата dd.MM.yyyy для DatePicker
  const startDateString = startDate ? format(startDate, 'dd.MM.yyyy') : undefined;
  const endDateString = endDate ? format(endDate, 'dd.MM.yyyy') : undefined;

  // Spectrum DatePicker отдаёт "промежуточные" строки при вводе — держим отдельный input-state,
  // иначе ввод будет откатываться до последней валидной даты.
  const [startDateInput, setStartDateInput] = useState<string | undefined>(startDateString);
  const [endDateInput, setEndDateInput] = useState<string | undefined>(endDateString);

  useEffect(() => {
    setStartDateInput(startDateString);
  }, [startDateString]);

  useEffect(() => {
    setEndDateInput(endDateString);
  }, [endDateString]);

  // Преобразуем типы событий в формат ComboboxOption
  const eventTypeOptions: ComboboxOption<IEventTypes>[] = useMemo(
    () =>
      eventTypes.map((event) => ({
        label: event.title,
        value: event,
      })),
    [eventTypes]
  );

  // Находим текущие выбранные опции типов событий
  const selectedEventTypeOptions = useMemo(
    () =>
      eventTypeOptions.filter((option) =>
        eventType.some((event) => event.id === option.value.id)
      ),
    [eventTypeOptions, eventType]
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '32px',
        }}
      >
        <Box sx={{ width: '180px' }}>
          <MultiCombobox<IEventTypes>
            label="Тип события"
            placeholder="Тип события"
            options={eventTypeOptions}
            value={selectedEventTypeOptions.map((opt) => opt.value)}
            searchable
            clearable
            onChange={(values) => {
              handleEventType(values || []);
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            width: '100%',
          }}
        >
          <Box sx={{ width: '160px' }}>
            <DatePicker
              mode="single"
              label="Период с"
              placeholder="Период с"
              value={startDateInput}
              onChange={(newValue) => {
                setStartDateInput(newValue || undefined);
                if (newValue) {
                  try {
                    // Парсим дату из формата dd.MM.yyyy
                    const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                    if (isValid(parsedDate) && isDateActual(parsedDate)) {
                      handleStartDate(parsedDate);
                    }
                  } catch {
                    // Если не удалось распарсить, игнорируем
                  }
                } else {
                  handleStartDate(null);
                }
              }}
              disabled={isLoadingCompanyActions}
              size="s"
            />
          </Box>

          <Box sx={{ width: '160px' }}>
            <DatePicker
              mode="single"
              label="Период до"
              placeholder="Период до"
              value={endDateInput}
              onChange={(newValue) => {
                setEndDateInput(newValue || undefined);
                if (newValue) {
                  try {
                    // Парсим дату из формата dd.MM.yyyy
                    const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                    if (isValid(parsedDate) && isDateActual(parsedDate)) {
                      handleEndDate(parsedDate);
                    }
                  } catch {
                    // Если не удалось распарсить, игнорируем
                  }
                } else {
                  handleEndDate(null);
                }
              }}
              disabled={isLoadingCompanyActions}
              size="s"
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ margin: '32px 0 16px' }}>
        <TableHistory
          historyActions={historyActions}
          isLoadingCompanyActions={isLoadingCompanyActions}
          hasNextPageCompanyActions={hasNextPageCompanyActions}
          isFetchingNextPageCompanyActions={isFetchingNextPageCompanyActions}
          fetchNextPageCompanyActions={fetchNextPageCompanyActions}
          orderingTableHistory={orderingTableHistory}
          handleOrderingTableHistory={handleOrderingTableHistory}
        />
      </Box>
    </>
  );
};
